/**
 * Brand values for code that cannot use React context.
 *
 * Site settings arrive at runtime from /api/settings/site and are consumed in
 * components through useSiteSettings(). But plain modules — seo.js, useSeo.js,
 * helpers called outside the React tree — cannot read a context, which is why
 * they ended up with hardcoded brand constants instead.
 *
 * This module is the bridge: SiteSettingsProvider pushes settings in here as
 * soon as they load, and non-React code reads them back. One place to update,
 * so the brand can never again be hardcoded in a dozen files.
 *
 * Usage:
 *   import { brandName, brandUrl } from '../lib/brand';
 *   const title = `${page} | ${brandName()}`;
 *
 * Do NOT cache the return value at module scope — settings load asynchronously,
 * so read it at call time or you will capture the placeholder.
 */

import { DEFAULT_SITE_NAME, DEFAULT_SUPPORT_EMAIL } from './siteDefaults';

let current = {
  siteName: DEFAULT_SITE_NAME,
  siteUrl: '',
  supportEmail: DEFAULT_SUPPORT_EMAIL,
  logoUrl: '',
  // Phone lives in Business Info (/api/settings/business), not site settings,
  // so it arrives via setBrandPhone rather than setBrand.
  phone: '',
};

const listeners = new Set();

/**
 * Subscribe to brand changes.
 *
 * Modules that keep their own copies of brand values (seo.js holds SITE_NAME
 * and SITE_URL so its 27 call sites stay unchanged) use this to refresh them
 * when settings arrive. Returns an unsubscribe function.
 *
 * Fires immediately with the current values, so a late subscriber is never
 * left holding the placeholder.
 */
export const subscribeBrand = (fn) => {
  listeners.add(fn);
  fn(getBrand());
  return () => listeners.delete(fn);
};

/** Called by SiteSettingsProvider once settings are fetched. */
export const setBrand = (settings = {}) => {
  current = {
    siteName: settings.siteName || current.siteName,
    siteUrl: settings.siteUrl || current.siteUrl,
    supportEmail: settings.supportEmail || current.supportEmail,
    logoUrl: settings.logoUrl || current.logoUrl,
    phone: current.phone,
  };
  listeners.forEach((fn) => fn(getBrand()));
};

/**
 * Called by the business-info fetch once Business Info loads.
 *
 * Kept separate from setBrand because the two values come from different
 * endpoints and arrive at different times; merging them into one setter would
 * let whichever resolved second blank the other.
 */
export const setBrandPhone = (phone) => {
  if (!phone || phone === current.phone) return;
  current = { ...current, phone };
  listeners.forEach((fn) => fn(getBrand()));
};

export const getBrand = () => ({ ...current });

export const brandName = () => current.siteName;
export const brandUrl = () => current.siteUrl;
export const brandSupportEmail = () => current.supportEmail;
export const brandLogoUrl = () => current.logoUrl;
export const brandPhone = () => current.phone;

/**
 * Absolute URL for canonical tags and Open Graph.
 * Falls back to the live origin when no site URL is configured, so canonicals
 * are never emitted pointing at someone else's domain.
 */
export const brandAbsoluteUrl = (path = '') => {
  const base =
    current.siteUrl ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  if (!base) return path;
  try {
    return new URL(path, base).href;
  } catch (e) {
    return path;
  }
};
